import { StreamCard } from "components/CaseManagement/CaseStream/StreamCard";
import { User } from "interfaces/user";
import React, { useContext, useState } from "react";
import { useFetchDeleteV2 } from "hooks/fetch/useFetchDeleteV2";
import { CaseEvent } from "interfaces/case-event";
import Skeleton from "@mui/material/Skeleton";
import { EventsContext } from "contexts/EventsContext";
import { CaseContext } from "contexts/CaseContext";
import { StreamCardAttachments } from "components/CaseManagement/CaseStream/StreamCards/StreamCardAttachments";
import Divider from "@mui/material/Divider";
import { EventCommentSection } from "components/CaseManagement/Comments/EventCommentSection";
import { styled } from "@mui/material/styles";
import ReportUpdateDialog from "components/CaseManagement/CaseStream/ReportUpdateDialog";
import { EditorStateToHtml } from "components/TextEditors/EditorStateToHtml";
import SeverityChip from "components/SeverityChip/SeverityChip";
import { ConfirmationDialog } from "components/ConfirmationDialog";
import { Box } from "@mui/system";
import { useEventAttachmentFilesQuery } from "hooks/queries/attachments";
import { CreateCaseActionDialog } from "components/CaseManagement/CaseForms/CreateCaseActionDialog";
import { useQueryClient } from "@tanstack/react-query";
import { CaseActionsContext } from "contexts/CaseActionsContext";

export interface ReportCardProps {
  caseEvent: CaseEvent;
  user: User;
  datetime: string;
  reportName: string;
  reportType: "inspection" | "diagnosis" | "repair";
  severity: number | null;
  refetchCaseEvent(): Promise<void>;
  refetchStream(): Promise<void>;
}

export const ReportCard = ({
  caseEvent,
  user,
  datetime,
  reportName,
  reportType,
  severity,
  refetchCaseEvent,
  refetchStream,
  children
}: React.PropsWithChildren<ReportCardProps>): JSX.Element => {
  const [editReportDialogOpen, setEditReportDialogOpen] = useState(false);
  const [deleteReportDialogOpen, setDeleteReportDialogOpen] = useState(false);
  const [createActionDialogOpen, setCreateActionDialogOpen] = useState(false);

  const queryClient = useQueryClient();

  const { loading, deleteCallback: deleteEvent } = useFetchDeleteV2(
    `events/${caseEvent.id}`
  );
  const { refetchEvents } = useContext(EventsContext);
  const { refetchCase } = useContext(CaseContext);
  const { refetchActions } = useContext(CaseActionsContext);

  const {
    isLoading: isAttachmentsLoading,
    isError: isAttachmentsError,
    data: attachmentsData
  } = useEventAttachmentFilesQuery({
    caseEventId: caseEvent.id
  });

  const handleDeleteCaseEvent = async (): Promise<void> => {
    try {
      await deleteEvent();
      await Promise.all([refetchCase(), refetchEvents(), refetchStream()]);
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) {
    return <Skeleton variant="rectangular" width={"100%"} height={200} />;
  }

  const menuOptions = [
    { title: "Edit", onClick: () => setEditReportDialogOpen(true) },
    { title: "Delete", onClick: () => setDeleteReportDialogOpen(true) }
  ];

  if (reportType === "diagnosis") {
    menuOptions.splice(1, 0, {
      title: "Create Linked Action",
      onClick: () => setCreateActionDialogOpen(true)
    });
  }

  return (
    <>
      <StreamCard
        user={user}
        datetime={datetime}
        label={reportName}
        headerAdornment={
          severity !== null && <SeverityChip severity={severity} />
        }
        menuOptions={menuOptions}
      >
        {children}
        <div>
          <SubtitleText>Description:</SubtitleText>
          <div>
            <EditorStateToHtml rawState={caseEvent.description} />
          </div>
          {caseEvent?.recommendation ? (
            <>
              <SubtitleText>Recommendation:</SubtitleText>
              <div>
                <EditorStateToHtml rawState={caseEvent.recommendation} />
              </div>
            </>
          ) : null}
          {!isAttachmentsLoading && !isAttachmentsError && (
            <Box>
              <strong>Attachments: </strong>
              <StreamCardAttachments
                attachments={attachmentsData}
                eventId={caseEvent.id}
              />
            </Box>
          )}
        </div>
        <Divider />
        <EventCommentSection eventId={caseEvent.id} />
      </StreamCard>

      {deleteReportDialogOpen && (
        <ConfirmationDialog
          title="Are you sure you want to delete this report?"
          open={deleteReportDialogOpen}
          onConfirm={() => {
            handleDeleteCaseEvent();
            setDeleteReportDialogOpen(false);
          }}
          onCancel={() => setDeleteReportDialogOpen(false)}
          onClose={() => setDeleteReportDialogOpen(false)}
        />
      )}

      {editReportDialogOpen && (
        <ReportUpdateDialog
          open={editReportDialogOpen}
          setOpen={setEditReportDialogOpen}
          reportName={reportName}
          reportType={reportType}
          report={caseEvent}
          refetchEvent={refetchCaseEvent}
          refetchStream={refetchStream}
        />
      )}

      {createActionDialogOpen && (
        <CreateCaseActionDialog
          open={createActionDialogOpen}
          setOpen={setCreateActionDialogOpen}
          caseId={caseEvent.case}
          addNewAction={async () => {
            await refetchActions();
            await queryClient.invalidateQueries(["streamFeed", caseEvent.case]);
          }}
          sourceEventId={caseEvent.id}
        />
      )}
    </>
  );
};

const SubtitleText = styled("div")`
  font-weight: bold;
`;
